<script setup>

import {onMounted, ref} from "vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const props = defineProps({
    buttonSize: {
        type: String,
        required: false,
        default: 'sm'
    },
    container: {
        type: String,
        required: false,
        default: 'div'
    },
    leadId: {
        type: Number,
        required: true
    }
});

const assignedTags = ref({})
const editing = ref(false)

const loadTags = async () => {
    const res = await axios.get(route('crm.leads.view.tags', props.leadId))
    assignedTags.value = res.data.tags;
}

const removeTag = async (tag) => {
    const res = await axios.delete(route('crm.leads.view.tags.remove', [props.leadId, tag.id]), {})
    await loadTags()
}

onMounted(() => {
    loadTags()
})

</script>

<template>
    <component :is="container">
        <template #title>
            <h3>{{ _mft('crm:tags') }}</h3>
        </template>
        <template #topRight>
            <button-secondary
                v-if="! editing"
                :size="props.buttonSize"
                @click="editing = true"
            >
                <mf-translate domainkey="crm:manageTags"></mf-translate>
            </button-secondary>

            <button-success
                v-if="container === 'card-default' && editing"
                @click="editing = false"
                size="sm"
            >
                <mf-translate domainkey="shared:done"></mf-translate>
            </button-success>
        </template>

        <div v-if="! editing">
            <p v-if="assignedTags.length === 0" class="text-center text-gray-500 text-xs">{{ _mft('crm:noTags') }}</p>
            <ul class="flex flex-wrap">
                <li
                    v-for="(tag, i) in assignedTags"
                    :key="i"
                    class="mb-1 mr-1"
                >
                    <pill
                        class="bg-gray-50 border border-gray-100 text-black flex items-center space-x-0.5"
                        size="sm"
                    >
                        <div
                            v-if="tag.color"
                            class="relative w-2.5 h-2.5 mr-1"
                        >
                            <div
                                class="absolute inset-0 w-2.5 h-2.5 rounded-full"
                                :style="{backgroundColor: tag.color, color: tag.textcolor}"
                            ></div>
                        </div>

                        <component
                            :is="tag.description ? 'container-with-tooltip' : 'span'"
                        >
                            <span>{{ tag.name }}</span>
                            <template
                                v-if="tag.description"
                                #tooltip
                            >
                                <div class="block w-24 text-xxs text-black">
                                    {{ tag.description }}
                                </div>
                            </template>
                        </component>
                    </pill>
                </li>
            </ul>
        </div>

        <div v-else>
            <div>
                <ul class="flex flex-wrap items-center">
                    <li
                        v-for="(tag, i) in assignedTags"
                        :key="i"
                        class="mb-1 mr-1"
                    >
                        <pill
                            class="bg-gray-50 border border-gray-100 text-black flex items-center space-x-0.5"
                            size="sm"
                        >
                            <div
                                v-if="tag.color"
                                class="relative w-2.5 h-2.5 mr-1"
                            >
                                <div
                                    class="absolute inset-0 w-2.5 h-2.5 rounded-full"
                                    :style="{backgroundColor: tag.color, color: tag.textcolor}"
                                ></div>
                            </div>

                            <span class="cursor-default">
                                <component
                                    :is="tag.description ? 'container-with-tooltip' : 'span'"
                                >
                                    {{ tag.name }}
                                    <template
                                        v-if="tag.description"
                                        #tooltip
                                    >
                                        <div class="block w-24 text-xxs">
                                            {{ tag.description }}
                                        </div>
                                    </template>
                                </component>
                            </span>

                            <span
                                class="bg-white hover:bg-gray-100 text-black w-4 h-4 rounded-full flex items-center justify-center cursor-pointer translate-x-1"
                                @click="removeTag(tag)"
                            >
                                <i class="fa-solid fa-xmark"></i>
                            </span>
                        </pill>
                    </li>
                </ul>
            </div>
            <div class="mt-2 pt-2 border-t">
                <tags-multi-assign-v2
                    :selected-leads="[props.leadId]"
                    :selected-tag-ids="assignedTags.map(tag => tag.id)"
                    :instant-assign="true"
                    @doReloadLeads="loadTags()"
                    :show-topbar="false"
                ></tags-multi-assign-v2>
            </div>
            <div class="flex justify-end mt-2">
                <button-success
                    v-if="container !== 'card-default'"
                    @click="editing = false"
                    size="sm"
                >
                    <mf-translate domainkey="shared:done"></mf-translate>
                </button-success>
            </div>
        </div>
    </component>
</template>

<style scoped>

</style>
